import React from 'react';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { Form, Row, Col } from 'react-bootstrap';
import queryString from 'query-string';
import moment from 'moment';
import ExpandingTextArea from 'react-expanding-textarea';

// Services & Helpers
import BaseService from 'services/BaseService';
import LocationService from 'services/LocationService';
import SubAgencyService from 'services/SubAgencyService';
import BootboxHelper from 'helpers/BootboxHelper';
import GlobalStateService from 'services/GlobalStateService';
import NotesTable from 'components/notes/NotesTable';

// Components
import Loader from 'components/common/Loader';

//-------------------------------------------------------------------------------------------------------------------

class BasicDetails extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        const { instructor, loginInfo } = this.props;
        const citiesReq = LocationService.listCities({
            countryID: instructor.countryID
        });
        const languagesReq = BaseService.callAPI('language/list');
        let subAgencies = null;
        if (loginInfo.isAdmin) {
            subAgencies = await SubAgencyService.list('sub-agency/list');
        }
        this.setState({
            cities: await citiesReq,
            languages: await languagesReq,
            subAgencies,
            isLoading: false
        });

        // Show welcome message if this is their first visit
        const qs = queryString.parse(window.location.search);
        if (qs['first-visit'] == 'true') {
            const loginInfo = GlobalStateService.getValue('loginInfo');
            if (loginInfo.instructor) {
                let msg;
                switch (loginInfo.instructor.type) {
                    case 'Approved':
                        msg = `<p>Hi ${loginInfo.instructor.firstName}, welcome to the team!</p>`;
                        msg += '<p>This is your profile on the <b>go courses Freelance Instructor Portal</b>.</p>';
                        msg += '<p>Here you can:</p>';
                        msg += '<ul>';
                        msg += '    <li>Amend personal details.</li>';
                        msg += '    <li>Enter your Availability.</li>';
                        msg += '    <li>Upload your Certifications and Course Enablements.</li>';
                        msg += '    <li>Review your Bookings.</li>';
                        msg += '    <li>Submit invoices.</li>';
                        msg += '</ul>';
                        msg += '<p>In the Onboarding Tab you will find the <b><i>Instructor Onboarding Process steps</i></b> – as you complete these with AWS, we just need you to tick the appropriate boxes on here too.</p>';
                        msg += '<p>Look forward to working with you!</p>';
                        break;
                    case 'Candidate':
                        msg = `<p>Hi ${loginInfo.instructor.firstName}, welcome to the team!</p>`;
                        msg += '<p>This is your profile on the <b>go courses Freelance Instructor Portal</b>.</p>';
                        msg += '<p>Here you can amend personal details and keep us updated on your progress in the Program</p>';
                        msg += '<p>In the Onboarding Tab you will find the <b><i>Instructor Onboarding Process steps</i></b> – as you complete these with AWS, we just need you to tick the appropriate boxes on here too.</p>';
                        msg += '<p>If you have any questions please "Request Support" or reach out to the team at <a href="mailto:aws@go.courses">aws@go.courses</a>.</p>';
                        msg += '<p>All the best with your qualification!</p>';
                        break;
                }
                if (msg) {
                    BootboxHelper.alert(msg);
                }
            }
        }
    }

    async save() {
        const { instructor, setIsBusy, setIsChanged, history } = this.props;
        setIsBusy(true);
        this.setState({ isLoading: true });
        try {
            const { slug } = await BaseService.callAPI('instructor/save-basic-details', instructor);
            setIsBusy(false);
            setIsChanged(false);
            this.setState({ isLoading: false });
            history.replace(`/instructor/${slug}`);
            BootboxHelper.alert('Changes saved');
        } catch (e) {
            BootboxHelper.alert(e);
            setIsBusy(false);
            setIsChanged(false);
            this.setState({ isLoading: false });
        }
    }

    updateFields(values) {
        const instructor = { ...this.props.instructor };

        for (var fieldName in values) {
            const value = values[fieldName];
            const oldValue = instructor[fieldName];
            instructor[fieldName] = value;
            switch (fieldName) {
                case 'countryID':
                    LocationService.listCities({
                        countryID: value
                    }).then(cities => {
                        this.setState({ cities });
                        if (cities.findIndex(c => c.countryID == instructor.cityID) == -1) {
                            this.updateFields({
                                cityID: null
                            });
                        }
                    });
                    instructor.country = this.props.countries.find(c => c.id == value);
                    break;
                case 'cityID':
                    instructor.city = this.state.cities.find(c => c.id == value) || null;
                    break;

                case 'status':
                    // Convert from candidate to instructor -> Automatically set to "Potential" active status
                    if (oldValue == 'Candidate' && value != 'Candidate' /*&& instructor.activeStatus != 'Active'*/) {
                        instructor.activeStatus = 'Potential';
                    }
                    break;
            }
        }
        this.props.onUpdate(instructor);
        this.props.setIsChanged(true, () => this.save());
    }

    toggleGeo(id) {
        const geoIDs = { ...this.props.instructor.geoIDs };
        if (geoIDs[id]) {
            delete geoIDs[id];
        } else {
            geoIDs[id] = true;
        }
        const newFields = { geoIDs };

        // Update primary geo
        const geoIDsList = Object.keys(geoIDs);
        if (geoIDsList.length == 1) {
            newFields.primaryGeoID = geoIDsList[0];
        } else if (geoIDsList.length == 0) {
            newFields.primaryGeoID = null;
        }

        this.updateFields(newFields);
    }

    updateWorkingDay(dow, isWorking) {
        const workingDays = [...this.props.instructor.workingDays];
        const index = workingDays.indexOf(dow);
        if (isWorking) {
            if (index == -1) {
                workingDays.push(dow);
            }
        } else if (index != -1) {
            workingDays.splice(index, 1);
        }
        this.updateFields({ workingDays });
    }

    //-----------------------------------------------------------------------------------------------

    render() {
        const { loginInfo, instructor, isEditMode } = this.props;
        const { isLoading } = this.state;
        if (isLoading) {
            return (<Loader fullPage />);
        }
        return (<>

            {loginInfo.isAdmin && <>
                <h2>Internal notes</h2>
                {!!instructor.id ?
                    <NotesTable
                        instructorID={instructor.id}
                        certID={0}
                        courseID={0}
                        showNewestFirst
                        allowShowAll
                        maxNum={5}
                        noteTypes={['General']}
                        newNoteType="General"
                    /> :
                    <ExpandingTextArea
                        className="form-control"
                        value={instructor.newNotes || ''}
                        onChange={e => this.updateFields({ newNotes: e.target.value })}
                        rows={3}
                    />
                }
            </>}

            {/*
            {loginInfo.isAdmin &&
                <Form.Group className="mb-3">
                    <Form.Label>Internal notes (Visible to Go Courses only)</Form.Label>
                    {isEditMode ?
                        <ExpandingTextArea
                            className="form-control"
                            value={instructor.internalNotes || ''}
                            onChange={e => this.updateFields({ internalNotes: e.target.value })}
                            rows={3}
                        /> :
                        <p>{instructor.internalNotes || '—'}</p>
                    }
                </Form.Group>
            }
            */}

            {this.renderContactDetails()}
            {this.renderDeliveryDetails()}
            {this.renderPersonalDetails()}
            
        </>);
    }

    renderContactDetails() {
        const { instructor, isEditMode, countries } = this.props;
        const { cities } = this.state;

        return (<>

            <h2>Contact details</h2>

            <Row>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        {isEditMode ?
                            <input
                                type="text"
                                className="form-control"
                                value={instructor.firstName || ''}
                                onChange={e => this.updateFields({ firstName: e.target.value })}
                            /> :
                            <p className="form-control-plaintext">
                                {instructor.firstName || <>—</>}
                            </p>
                        }
                    </Form.Group>

                </Col>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        {isEditMode ?
                            <input
                                type="text"
                                className="form-control"
                                value={instructor.lastName || ''}
                                onChange={e => this.updateFields({ lastName: e.target.value })}
                            /> :
                            <p className="form-control-plaintext">
                                {instructor.lastName || <>—</>}
                            </p>
                        }
                    </Form.Group>

                </Col>

            </Row>

            <Row>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>Email Address</Form.Label>
                        {isEditMode ?
                            <input
                                type="email"
                                className="form-control"
                                value={instructor.email || ''}
                                onChange={e => this.updateFields({ email: e.target.value })}
                            /> :
                            <p className="form-control-plaintext">
                                {instructor.email || <>—</>}
                            </p>
                        }
                    </Form.Group>
                    
                </Col>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>LinkedIn Profile</Form.Label>
                        {isEditMode ?
                            <div className="input-group mb-3">
                                <input
                                    type="url"
                                    className="form-control"
                                    value={instructor.linkedInURL || ''}
                                    onChange={e => this.updateFields({ linkedInURL: e.target.value })}
                                />
                                {!!instructor.linkedInURL &&
                                    <div className="input-group-append">
                                        <a href={instructor.linkedInURL} className="btn btn-outline-secondary" target="_blank">
                                            <span className="fa-solid fa-chevron-right" />
                                        </a>
                                    </div>
                                }
                            </div> :
                            <p className="form-control-plaintext">
                                {instructor.linkedInURL ? <a href={instructor.linkedInURL} target="_blank">{instructor.linkedInURL}</a> : <>—</>}
                            </p>
                        }
                    </Form.Group>

                </Col>

            </Row>

            <Row>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        {isEditMode ?
                            <Select
                                isClearable={false}
                                options={countries}
                                value={instructor.country}
                                getOptionValue={c => c.id}
                                getOptionLabel={c => c.name}
                                onChange={country => this.updateFields({ countryID: country.id })}
                            /> :
                            <p>{(instructor.country ? instructor.country.name : '') || <>—</>}</p>
                        }
                    </Form.Group>

                </Col>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>City</Form.Label>
                        {isEditMode ?
                            <Creatable
                                isClearable={false}
                                options={cities}
                                value={instructor.city}
                                getOptionValue={c => c.id}
                                getOptionLabel={b => b.__isNew__ ? b.label : b.name}
                                formatCreateLabel={name => <>Add <b>{name}</b></>}
                                onChange={async city => {
                                    if (city.__isNew__) {
                                        if (instructor.countryID) {
                                            city.countryID = instructor.countryID;
                                            city.name = city.label;
                                            const response = await LocationService.addCity(city);
                                            city.id = response.id;
                                            const cities = [...this.state.cities];
                                            cities.push(city);
                                            this.setState({
                                                cities
                                            }, () => {
                                                this.updateFields({ cityID: city.id })
                                            });
                                        } else {
                                            BootboxHelper.alert('Please select a country first when adding a new city');
                                        }
                                    } else {
                                        this.updateFields({ cityID: city.id })
                                    }
                                }}
                            /> :
                            <p>{(instructor.city ? instructor.city.name : '') || <>—</>}</p>
                        }
                    </Form.Group>

                </Col>

            </Row>
            
        </>);
    }

    renderPersonalDetails() {
        const { instructor, isEditMode, countries } = this.props;

        return (<>

            <h2>Personal details</h2>
            
            <Row>

                <Col md="3">

                    {isEditMode &&
                        <Form.Group className="mb-0">
                            <Form.Label>Date of Birth</Form.Label>
                            <input
                                type="number"
                                className="form-control"
                                value={instructor.dateOfBirthDay || ''}
                                onChange={e => this.updateFields({ dateOfBirthDay: e.target.value })}
                                placeholder="Day"
                            />
                        </Form.Group>
                    }

                </Col>

                <Col md="3">

                    <Form.Group className="mb-0">
                        <Form.Label>&nbsp;</Form.Label>
                        {isEditMode ?
                            <select
                                className="form-control"
                                value={instructor.dateOfBirthMonth || ''}
                                onChange={e => this.updateFields({ dateOfBirthMonth: e.target.value })}
                            >
                                <option value="">(Select)</option>
                                <option value="1">January</option>
                                <option value="2">February</option>
                                <option value="3">March</option>
                                <option value="4">April</option>
                                <option value="5">May</option>
                                <option value="6">June</option>
                                <option value="7">July</option>
                                <option value="8">August</option>
                                <option value="9">September</option>
                                <option value="10">October</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select> :
                            <p>
                                {(instructor.dateOfBirthMonth || instructor.dateOfBirthDay) ?
                                    (instructor.dateOfBirthDay + ' ' + (instructor.dateOfBirthMonth ? moment(instructor.dateOfBirthMonth, 'M').format('MMMM') : '')) :
                                    <>—</>
                                }
                            </p>
                        }
                    </Form.Group>

                </Col>

                <Col md="6">

                    <Form.Group className="mb-0">
                        <Form.Label>Interests Outside of Work</Form.Label>
                        {isEditMode ?
                            <ExpandingTextArea
                                className="form-control"
                                value={instructor.otherInterests || ''}
                                onChange={e => this.updateFields({ otherInterests: e.target.value })}
                                rows={3}
                            /> :
                            <p>{instructor.otherInterests || '—'}</p>
                        }
                    </Form.Group>

                </Col>

            </Row>

        </>);
    }

    renderDeliveryDetails() {
        const { instructor, isEditMode, loginInfo, geos } = this.props;
        const { languages, subAgencies } = this.state;
        const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

        return (<>

            <h2>Delivery details</h2>

            {loginInfo.isVendorOrAdmin &&

                <Row>

                    <Col md="2">

                        <Form.Group className="mb-3">
                            <Form.Label>Type</Form.Label>
                            {isEditMode ?
                                <select
                                    className="form-control"
                                    value={instructor.type || ''}
                                    onChange={e => this.updateFields({ type: e.target.value })}
                                >
                                    <option value="Prospect">Prospect</option>
                                    <option value="Candidate">Candidate</option>
                                    <option value="Approved">Approved</option>
                                </select> :
                                <p>{instructor.type}</p>
                            }
                        </Form.Group>

                    </Col>

                    <Col md="2">

                        <Form.Group className="mb-3">
                            <Form.Label>Instructor Status</Form.Label>
                            {isEditMode ?
                                <select
                                    className="form-control"
                                    value={instructor.status || ''}
                                    onChange={e => this.updateFields({ status: e.target.value })}
                                >
                                    <option value="Standard">Standard</option>
                                    <option value="Champion">Champion</option>
                                </select> :
                                <p>{instructor.status}</p>
                            }
                        </Form.Group>

                    </Col>

                    {instructor.type == 'Prospect' &&
                        <Col md="2">

                            <Form.Group className="mb-3">
                                <Form.Label>Prospect Type</Form.Label>
                                {isEditMode ?
                                    <select
                                        className="form-control"
                                        value={instructor.prospectType || ''}
                                        onChange={e => this.updateFields({ prospectType: e.target.value })}
                                    >
                                        <option value="">(Select)</option>
                                        <option value="Candidate">Candidate</option>
                                        <option value="Approved">Approved</option>
                                    </select> :
                                    <p>{instructor.prospectType}</p>
                                }
                            </Form.Group>

                        </Col>
                    }

                    <Col md="2">

                        <Form.Group className="mb-3">
                            <Form.Label>Active Status</Form.Label>
                            {isEditMode ?
                                <select
                                    className="form-control"
                                    value={instructor.activeStatus || ''}
                                    onChange={e => this.updateFields({ activeStatus: e.target.value })}
                                >
                                    <option value="">(Select)</option>
                                    <option value="Pending">Pending</option>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                    <option value="Potential">Potential</option>
                                    <option value="Deactivated">Deactivated</option>
                                    <option value="Suspended">Suspended</option>
                                </select> :
                                <p>{instructor.activeStatus}</p>
                            }
                        </Form.Group>


                    </Col>

                    {loginInfo.isAdmin &&
                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Sub-agency</Form.Label>

                                {isEditMode ?
                                    <select
                                        className="form-control"
                                        value={instructor.subAgencyID}
                                        onChange={e => this.updateFields({ subAgencyID: e.target.value })}
                                    >
                                        <option value="">(Please select...)</option>
                                        <option value="0">(None)</option>
                                        {subAgencies.map(sa =>
                                            <option key={sa.id} value={sa.id}>
                                                {sa.name}
                                            </option>
                                        )}
                                    </select> :
                                    <p>{instructor.subAgencyID}</p>
                                }
                            </Form.Group>

                        </Col>
                    }
                </Row>

            }

            <Row>

                <Col md="6">

                    <Form.Group className="mb-3">
                        <Form.Label>Languages</Form.Label>
                        {isEditMode ?
                            <Select
                                isMulti
                                isClearable={false}
                                value={instructor.languages}
                                options={languages}
                                getOptionValue={l => l.id}
                                getOptionLabel={l => l.name}
                                onChange={v => this.updateFields({ languages: v })}
                            /> :
                            <p>{instructor.languages.map(l => l.name).join(', ') || <>—</>}</p>
                        }
                    </Form.Group>

                    {loginInfo.isVendorOrAdmin &&
                        <Form.Group className="mb-3">
                            <Form.Label>Working Days</Form.Label>
                            {isEditMode ? <>
                                {daysOfWeek.map(dow =>
                                    <Form.Check
                                        key={dow}
                                        type="checkbox"
                                        label={dow}
                                        id={`working-days-${dow}`}
                                        checked={instructor.workingDays.indexOf(dow) != -1 || false}
                                        onChange={e => this.updateWorkingDay(dow, e.target.checked)}
                                    />
                                )}
                            </> : <>
                                <p>{instructor.workingDays.join(', ')}</p>
                            </>}
                        </Form.Group>
                    }
                </Col>

                <Col md="6">

                    <Row>

                        <Col md="6">

                            <Form.Group className="mb-3">
                                <Form.Label>Available to Teach...</Form.Label>
                                {isEditMode ? <>
                                    <Form.Check
                                        type="checkbox"
                                        label="ILT"
                                        id="available-ilt"
                                        checked={instructor.isAvailableILT || false}
                                        onChange={e => this.updateFields({ isAvailableILT: e.target.checked })}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="vILT"
                                        id="available-vilt"
                                        checked={instructor.isAvailableVILT || false}
                                        onChange={e => this.updateFields({ isAvailableVILT: e.target.checked })}
                                    />
                                </> :
                                    <p>{instructor.languages.map(l => l.name).join(', ') || <>—</>}</p>
                                }
                            </Form.Group>

                            {instructor.type != 'Candidate' && loginInfo.isVendorOrAdmin && <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Priority</Form.Label>
                                    {isEditMode ? <>
                                        <Form.Check
                                            type="checkbox"
                                            label={<>Priority instructor (<span className="fa fa-star" />)</>}
                                            id="is-priority"
                                            checked={instructor.isPriority || false}
                                            onChange={e => this.updateFields({ isPriority: e.target.checked })}
                                        />
                                    </> : <>
                                        <p>{instructor.isPriority ? <>Priority instructor (<span className="fa fa-star" />)</> : ''}</p>
                                    </>}
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Priority for...</Form.Label>
                                    <Form.Check
                                        type="checkbox"
                                        label="Q4'22"
                                        id="priority-q422"
                                        checked={instructor.isPriorityQ422 || false}
                                        readOnly={!isEditMode}
                                        onChange={e => isEditMode ? this.updateFields({ isPriorityQ422: e.target.checked }) : null}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="Q1'23"
                                        id="priority-q123"
                                        checked={instructor.isPriorityQ123 || false}
                                        readOnly={!isEditMode}
                                        onChange={e => isEditMode ? this.updateFields({ isPriorityQ123: e.target.checked }) : null}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="Q2'23"
                                        id="priority-q223"
                                        checked={instructor.isPriorityQ223 || false}
                                        readOnly={!isEditMode}
                                        onChange={e => isEditMode ? this.updateFields({ isPriorityQ223: e.target.checked }) : null}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="Q3'23"
                                        id="priority-q323"
                                        checked={instructor.isPriorityQ323 || false}
                                        readOnly={!isEditMode}
                                        onChange={e => isEditMode ? this.updateFields({ isPriorityQ323: e.target.checked }) : null}
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label="Q4'23"
                                        id="priority-q423"
                                        checked={instructor.isPriorityQ423 || false}
                                        readOnly={!isEditMode}
                                        onChange={e => isEditMode ? this.updateFields({ isPriorityQ423: e.target.checked }) : null}
                                    />
                                </Form.Group>
                            </>}

                        </Col>

                        {loginInfo.isAdmin &&
                            <Col md="6">

                                <Form.Group className="mb-3">
                                    <Form.Label>Geos Permitted</Form.Label>

                                    {isEditMode ? geos.map(geo =>
                                        <Form.Check
                                            key={geo.id}
                                            type="checkbox"
                                            label={geo.name}
                                            id={`geo-${geo.id}`}
                                            checked={instructor.geoIDs[geo.id] || false}
                                            readOnly={!isEditMode}
                                            onChange={e => this.toggleGeo(geo.id)}
                                        />
                                    ) :
                                        <p className="form-control-plaintext">
                                            {Object.keys(instructor.geoIDs).map(id => geos.find(g => g.id == id).name).join(', ')}
                                        </p>
                                    }
                                </Form.Group>

                                <Form.Group className="">
                                    <Form.Label>Primary Geo</Form.Label>

                                    <select
                                        className="form-control"
                                        readOnly={!isEditMode}
                                        value={instructor.primaryGeoID || ''}
                                        onChange={e => this.updateFields({ primaryGeoID: e.target.value })}
                                    >
                                        <option value="">(Please select)</option>
                                        {geos.filter(g => instructor.geoIDs[g.id]).map(geo =>
                                            <option key={geo.id} value={geo.id}>
                                                {geo.name}
                                            </option>
                                        )}
                                    </select>
                                </Form.Group>

                            </Col>
                        }

                    </Row>

                </Col>

            </Row>

        </>);
    }

}

export default BasicDetails;